import type { LucideIcon } from 'lucide-react'
import { Sparkles, Shield, Droplets, Leaf, Star } from 'lucide-react'
import type { I18nKey } from './types'

// FULLY MIGRATED (config→JSON i18n, scripts/i18n-domains/trichology.mjs):
// every localised field is an I18nKey ('trichology:…' string) resolved through
// loc()'s i18next bridge; the copy lives in public/locales/<lang>/trichology.json.
// No LocalisedString literals remain in this file - all consumer components
// (the 11 trichology sections + TrichologyPage/TrichologyDetailPage) read via loc().

export interface TrichologyProcessStep {
  title: I18nKey
  desc: I18nKey
}

export interface TrichologySymptom {
  text: I18nKey
}

export interface TrichologyScienceItem {
  /** Short label, e.g. "Sweet Orange" or "Saw Palmetto" */
  name: I18nKey
  /** Optional product code shown as eyebrow, e.g. "energy 03.1" */
  code?: string
  /** Best for / role */
  bestFor: I18nKey
  /** What it does */
  benefit: I18nKey
  /** Optional decorative image (transparent PNG/WebP) shown faintly on the card. */
  image?: string
}

export interface TrichologyRitualStep {
  index: string
  title: I18nKey
  desc: I18nKey
}

export interface TrichologyTimelineStep {
  label: I18nKey
  desc: I18nKey
}

export interface TrichologyPricingTier {
  /** Internal id used for keys */
  id: 'diagnosis' | 'single' | 'course'
  label: I18nKey
  price: I18nKey
  /** Short summary line, e.g. "60 min · 1 visit" */
  includes: I18nKey
  /** Optional meta chip above the title (e.g. "60 min · 1 visit") */
  meta?: I18nKey
  /** Optional editorial tagline shown under the meta chip in the left column */
  tagline?: I18nKey
  /** Optional subtotal under price (e.g. "799 DKK / session · pre-paid") */
  subtotal?: I18nKey
  /** Optional badge label (overrides default 'pricingNote' translation) */
  badge?: I18nKey
  /** Visual emphasis tier */
  highlight?: boolean
}

export interface TrichologyProductCard {
  code: string
  name: I18nKey
  size: I18nKey
  role: I18nKey
  /** Slug or path on shop, appended after /:lang/shop */
  shopPath?: string
}

export interface TrichologyFaqItem {
  q: I18nKey
  a: I18nKey
}

export interface TrichologyBrandStory {
  eyebrow: I18nKey
  title: I18nKey
  body: I18nKey
}

export interface TrichologyDetail {
  slug: string
  number: string
  name: string
  icon: LucideIcon
  image: string
  hero: {
    eyebrow: I18nKey
    title: I18nKey
    subtitle: I18nKey
    price: I18nKey
    duration: I18nKey
  }
  about: {
    description: I18nKey
    philosophyTitle: I18nKey
    philosophy: I18nKey
  }
  benefits: I18nKey[]
  process: TrichologyProcessStep[]
  aftercare: I18nKey
  idealFor: I18nKey

  // ----- Optional, presence-gated upgrade fields -----
  /** Headline used in the new hero (overrides hero.title when present). */
  heroPromise?: I18nKey
  /** Subline for the new hero. */
  heroSubline?: I18nKey
  /** Optional per-treatment hero image. When set, the upgraded hero uses this still
   *  instead of the shared trichology hero video. */
  heroImage?: string
  /** Optional CSS object-position for the hero image, e.g. "70% 35%". */
  heroImagePosition?: string
  /** Trust strip cards under the hero CTAs (Membership-style perks grid). */
  heroBadges?: Array<{
    icon: 'microscope' | 'clock' | 'award' | 'pin'
    title: I18nKey
    body: I18nKey
  }>
  /** Symptoms checklist - drives self-identification. */
  symptoms?: {
    title: I18nKey
    intro?: I18nKey
    items: TrichologySymptom[]
  }
  /** Science cards (e.g. essential oils mapped to hair fall types). */
  science?: {
    eyebrow: I18nKey
    title: I18nKey
    intro: I18nKey
    anchor?: { name: I18nKey; benefit: I18nKey }
    items: TrichologyScienceItem[]
  }
  /** Brand / heritage story block. */
  brandStory?: TrichologyBrandStory
  /** Detailed in-salon ritual (replaces or augments `process`). */
  ritual?: {
    eyebrow: I18nKey
    title: I18nKey
    intro?: I18nKey
    steps: TrichologyRitualStep[]
    image?: string
    secondaryImage?: string
    /** Optional closing line shown centred below the ritual steps. */
    outro?: I18nKey
  }
  /** What to expect timeline. */
  timeline?: {
    eyebrow: I18nKey
    title: I18nKey
    note?: I18nKey
    items: TrichologyTimelineStep[]
    /** Optional brand closer line shown centred below the timeline grid. */
    closer?: I18nKey
  }
  /** Pricing tiers - diagnosis / single / course. */
  pricing?: {
    eyebrow: I18nKey
    title: I18nKey
    note?: I18nKey
    tiers: TrichologyPricingTier[]
    /** Optional "not sure where to start?" closer shown below the tiers. */
    closer?: { title: I18nKey; body: I18nKey }
  }
  /** Take-it-home product range. Gated by SHOP feature flag. */
  homeRoutine?: {
    eyebrow: I18nKey
    title: I18nKey
    intro?: I18nKey
    products: TrichologyProductCard[]
  }
  /** FAQ accordion. */
  faq?: {
    eyebrow: I18nKey
    title: I18nKey
    items: TrichologyFaqItem[]
  }
  /** Beauty-cases atmosphere gallery (image URLs). */
  gallery?: string[]
  /** Aspect ratio for the About-section flatlay image, e.g. "1041/745" (landscape) or "1302/1224" (square-ish).
   *  Match the source image's native aspect so the subject isn't cropped. Defaults to "1041/745" (Energy). */
  aboutImageAspect?: string
  /** Per-treatment FinalCTA copy override. Falls back to shared translation keys. */
  finalCta?: {
    title: I18nKey
    note: I18nKey
    bookTreatment?: I18nKey
    exploreOther?: I18nKey
  }
}

export const trichologyDetails: TrichologyDetail[] = [
  {
    slug: 'energy',
    number: '01',
    name: 'Energy',
    icon: Sparkles,
    image: '/images/trichology/energy/ritual-shell.webp',
    hero: {
      eyebrow: 'trichology:energy.hero.eyebrow',
      title: 'trichology:energy.hero.title',
      subtitle: 'trichology:energy.hero.subtitle',
      price: 'trichology:energy.hero.price',
      duration: 'trichology:energy.hero.duration',
    },
    about: {
      description: 'trichology:energy.about.description',
      philosophyTitle: 'trichology:energy.about.philosophyTitle',
      philosophy: 'trichology:energy.about.philosophy',
    },
    benefits: [
      'trichology:energy.benefits.0',
      'trichology:energy.benefits.1',
      'trichology:energy.benefits.2',
      'trichology:energy.benefits.3',
      'trichology:energy.benefits.4',
      'trichology:energy.benefits.5',
      'trichology:energy.benefits.6',
    ],
    process: [
      {
        title: 'trichology:energy.process.0.title',
        desc: 'trichology:energy.process.0.desc',
      },
      {
        title: 'trichology:energy.process.1.title',
        desc: 'trichology:energy.process.1.desc',
      },
      {
        title: 'trichology:energy.process.2.title',
        desc: 'trichology:energy.process.2.desc',
      },
      {
        title: 'trichology:energy.process.3.title',
        desc: 'trichology:energy.process.3.desc',
      },
    ],
    aftercare: 'trichology:energy.aftercare',
    idealFor: 'trichology:energy.idealFor',

    // ----- Upgrade fields -----
    heroPromise: 'trichology:energy.heroPromise',
    heroSubline: 'trichology:energy.heroSubline',
    heroImage: '/images/trichology/energy/hero-muran.webp',
    heroImagePosition: '70% 45%',
    heroBadges: [
      {
        icon: 'microscope',
        title: 'trichology:energy.heroBadges.0.title',
        body: 'trichology:energy.heroBadges.0.body',
      },
      {
        icon: 'clock',
        title: 'trichology:energy.heroBadges.1.title',
        body: 'trichology:energy.heroBadges.1.body',
      },
      {
        icon: 'award',
        title: 'trichology:energy.heroBadges.2.title',
        body: 'trichology:energy.heroBadges.2.body',
      },
    ],

    symptoms: {
      title: 'trichology:energy.symptoms.title',
      intro: 'trichology:energy.symptoms.intro',
      items: [
        { text: 'trichology:energy.symptoms.items.0.text' },
        { text: 'trichology:energy.symptoms.items.1.text' },
        { text: 'trichology:energy.symptoms.items.2.text' },
        { text: 'trichology:energy.symptoms.items.3.text' },
        { text: 'trichology:energy.symptoms.items.4.text' },
        { text: 'trichology:energy.symptoms.items.5.text' },
        { text: 'trichology:energy.symptoms.items.6.text' },
        { text: 'trichology:energy.symptoms.items.7.text' },
      ],
    },

    science: {
      eyebrow: 'trichology:energy.science.eyebrow',
      title: 'trichology:energy.science.title',
      intro: 'trichology:energy.science.intro',
      anchor: {
        name: 'trichology:energy.science.anchor.name',
        benefit: 'trichology:energy.science.anchor.benefit',
      },
      items: [
        {
          name: 'trichology:energy.science.items.0.name',
          code: 'energy 03.1',
          bestFor: 'trichology:energy.science.items.0.bestFor',
          benefit: 'trichology:energy.science.items.0.benefit',
          image: '/images/trichology/energy/oils/orange.webp',
        },
        {
          name: 'trichology:energy.science.items.1.name',
          code: 'energy 03.2',
          bestFor: 'trichology:energy.science.items.1.bestFor',
          benefit: 'trichology:energy.science.items.1.benefit',
          image: '/images/trichology/energy/oils/sage.webp',
        },
        {
          name: 'trichology:energy.science.items.2.name',
          code: 'energy 03.3',
          bestFor: 'trichology:energy.science.items.2.bestFor',
          benefit: 'trichology:energy.science.items.2.benefit',
          image: '/images/trichology/energy/oils/juniper.webp',
        },
      ],
    },

    brandStory: {
      eyebrow: 'trichology:energy.brandStory.eyebrow',
      title: 'trichology:energy.brandStory.title',
      body: 'trichology:energy.brandStory.body',
    },

    ritual: {
      eyebrow: 'trichology:energy.ritual.eyebrow',
      title: 'trichology:energy.ritual.title',
      image: '/images/trichology/energy/ritual-shell.webp',
      secondaryImage: '/images/trichology/energy/ritual-massage.webp',
      outro: 'trichology:energy.ritual.outro',
      steps: [
        {
          index: '01',
          title: 'trichology:energy.ritual.steps.0.title',
          desc: 'trichology:energy.ritual.steps.0.desc',
        },
        {
          index: '02',
          title: 'trichology:energy.ritual.steps.1.title',
          desc: 'trichology:energy.ritual.steps.1.desc',
        },
        {
          index: '03',
          title: 'trichology:energy.ritual.steps.2.title',
          desc: 'trichology:energy.ritual.steps.2.desc',
        },
        {
          index: '04',
          title: 'trichology:energy.ritual.steps.3.title',
          desc: 'trichology:energy.ritual.steps.3.desc',
        },
      ],
    },

    timeline: {
      eyebrow: 'trichology:energy.timeline.eyebrow',
      title: 'trichology:energy.timeline.title',
      note: 'trichology:energy.timeline.note',
      closer: 'trichology:energy.timeline.closer',
      items: [
        {
          label: 'trichology:energy.timeline.items.0.label',
          desc: 'trichology:energy.timeline.items.0.desc',
        },
        {
          label: 'trichology:energy.timeline.items.1.label',
          desc: 'trichology:energy.timeline.items.1.desc',
        },
        {
          label: 'trichology:energy.timeline.items.2.label',
          desc: 'trichology:energy.timeline.items.2.desc',
        },
        {
          label: 'trichology:energy.timeline.items.3.label',
          desc: 'trichology:energy.timeline.items.3.desc',
        },
      ],
    },

    pricing: {
      eyebrow: 'trichology:energy.pricing.eyebrow',
      title: 'trichology:energy.pricing.title',
      closer: {
        title: 'trichology:energy.pricing.closer.title',
        body: 'trichology:energy.pricing.closer.body',
      },
      tiers: [
        {
          id: 'diagnosis',
          label: 'trichology:energy.pricing.tiers.diagnosis.label',
          price: 'trichology:energy.pricing.tiers.diagnosis.price',
          includes: 'trichology:energy.pricing.tiers.diagnosis.includes',
          meta: 'trichology:energy.pricing.tiers.diagnosis.meta',
          tagline: 'trichology:energy.pricing.tiers.diagnosis.tagline',
        },
        {
          id: 'single',
          label: 'trichology:energy.pricing.tiers.single.label',
          price: 'trichology:energy.pricing.tiers.single.price',
          includes: 'trichology:energy.pricing.tiers.single.includes',
          meta: 'trichology:energy.pricing.tiers.single.meta',
          tagline: 'trichology:energy.pricing.tiers.single.tagline',
          badge: 'trichology:energy.pricing.tiers.single.badge',
        },
        {
          id: 'course',
          label: 'trichology:energy.pricing.tiers.course.label',
          price: 'trichology:energy.pricing.tiers.course.price',
          includes: 'trichology:energy.pricing.tiers.course.includes',
          meta: 'trichology:energy.pricing.tiers.course.meta',
          tagline: 'trichology:energy.pricing.tiers.course.tagline',
          badge: 'trichology:energy.pricing.tiers.course.badge',
          highlight: true,
        },
      ],
    },

    homeRoutine: {
      eyebrow: 'trichology:energy.homeRoutine.eyebrow',
      title: 'trichology:energy.homeRoutine.title',
      intro: 'trichology:energy.homeRoutine.intro',
      products: [
        {
          code: 'energy 01',
          name: 'trichology:energy.homeRoutine.products.0.name',
          size: 'trichology:energy.homeRoutine.products.0.size',
          role: 'trichology:energy.homeRoutine.products.0.role',
          shopPath: 'muran/energy-01',
        },
        {
          code: 'energy 02',
          name: 'trichology:energy.homeRoutine.products.1.name',
          size: 'trichology:energy.homeRoutine.products.1.size',
          role: 'trichology:energy.homeRoutine.products.1.role',
          shopPath: 'muran/energy-02',
        },
        {
          code: 'energy 04',
          name: 'trichology:energy.homeRoutine.products.2.name',
          size: 'trichology:energy.homeRoutine.products.2.size',
          role: 'trichology:energy.homeRoutine.products.2.role',
          shopPath: 'muran/energy-04',
        },
        {
          code: 'energy 06',
          name: 'trichology:energy.homeRoutine.products.3.name',
          size: 'trichology:energy.homeRoutine.products.3.size',
          role: 'trichology:energy.homeRoutine.products.3.role',
          shopPath: 'muran/energy-06',
        },
      ],
    },

    faq: {
      eyebrow: 'trichology:energy.faq.eyebrow',
      title: 'trichology:energy.faq.title',
      items: [
        {
          q: 'trichology:energy.faq.items.0.q',
          a: 'trichology:energy.faq.items.0.a',
        },
        {
          q: 'trichology:energy.faq.items.1.q',
          a: 'trichology:energy.faq.items.1.a',
        },
        {
          q: 'trichology:energy.faq.items.2.q',
          a: 'trichology:energy.faq.items.2.a',
        },
        {
          q: 'trichology:energy.faq.items.3.q',
          a: 'trichology:energy.faq.items.3.a',
        },
        {
          q: 'trichology:energy.faq.items.4.q',
          a: 'trichology:energy.faq.items.4.a',
        },
        {
          q: 'trichology:energy.faq.items.5.q',
          a: 'trichology:energy.faq.items.5.a',
        },
        {
          q: 'trichology:energy.faq.items.6.q',
          a: 'trichology:energy.faq.items.6.a',
        },
        {
          q: 'trichology:energy.faq.items.7.q',
          a: 'trichology:energy.faq.items.7.a',
        },
        {
          q: 'trichology:energy.faq.items.8.q',
          a: 'trichology:energy.faq.items.8.a',
        },
      ],
    },

    gallery: [
      '/images/trichology/energy/case-01.webp',
      '/images/trichology/energy/case-02.webp',
      '/images/trichology/energy/case-03.webp',
      '/images/trichology/energy/case-04.webp',
      '/images/trichology/energy/case-05.webp',
    ],
    finalCta: {
      title: 'trichology:energy.finalCta.title',
      note: 'trichology:energy.finalCta.note',
      bookTreatment: 'trichology:energy.finalCta.bookTreatment',
      exploreOther: 'trichology:energy.finalCta.exploreOther',
    },
  },
  {
    slug: 'purify',
    number: '02',
    name: 'Purify',
    icon: Droplets,
    image: '/images/trichology/purify/about-flatlay-v3.webp',
    aboutImageAspect: '1022/960',
    hero: {
      eyebrow: 'trichology:purify.hero.eyebrow',
      title: 'trichology:purify.hero.title',
      subtitle: 'trichology:purify.hero.subtitle',
      price: 'trichology:purify.hero.price',
      duration: 'trichology:purify.hero.duration',
    },
    about: {
      description: 'trichology:purify.about.description',
      philosophyTitle: 'trichology:purify.about.philosophyTitle',
      philosophy: 'trichology:purify.about.philosophy',
    },
    benefits: [
      'trichology:purify.benefits.0',
      'trichology:purify.benefits.1',
      'trichology:purify.benefits.2',
      'trichology:purify.benefits.3',
    ],
    process: [
      {
        title: 'trichology:purify.process.0.title',
        desc: 'trichology:purify.process.0.desc',
      },
      {
        title: 'trichology:purify.process.1.title',
        desc: 'trichology:purify.process.1.desc',
      },
      {
        title: 'trichology:purify.process.2.title',
        desc: 'trichology:purify.process.2.desc',
      },
      {
        title: 'trichology:purify.process.3.title',
        desc: 'trichology:purify.process.3.desc',
      },
    ],
    aftercare: 'trichology:purify.aftercare',
    idealFor: 'trichology:purify.idealFor',

    // ----- Upgrade fields -----
    heroPromise: 'trichology:purify.heroPromise',
    heroSubline: 'trichology:purify.heroSubline',
    heroImage: '/images/trichology/purify/hero-muran.webp',
    heroImagePosition: 'center center',
    heroBadges: [
      {
        icon: 'microscope',
        title: 'trichology:purify.heroBadges.0.title',
        body: 'trichology:purify.heroBadges.0.body',
      },
      {
        icon: 'clock',
        title: 'trichology:purify.heroBadges.1.title',
        body: 'trichology:purify.heroBadges.1.body',
      },
      {
        icon: 'award',
        title: 'trichology:purify.heroBadges.2.title',
        body: 'trichology:purify.heroBadges.2.body',
      },
    ],

    symptoms: {
      title: 'trichology:purify.symptoms.title',
      intro: 'trichology:purify.symptoms.intro',
      items: [
        { text: 'trichology:purify.symptoms.items.0.text' },
        { text: 'trichology:purify.symptoms.items.1.text' },
        { text: 'trichology:purify.symptoms.items.2.text' },
        { text: 'trichology:purify.symptoms.items.3.text' },
        { text: 'trichology:purify.symptoms.items.4.text' },
        { text: 'trichology:purify.symptoms.items.5.text' },
        { text: 'trichology:purify.symptoms.items.6.text' },
      ],
    },

    science: {
      eyebrow: 'trichology:purify.science.eyebrow',
      title: 'trichology:purify.science.title',
      intro: 'trichology:purify.science.intro',
      anchor: {
        name: 'trichology:purify.science.anchor.name',
        benefit: 'trichology:purify.science.anchor.benefit',
      },
      items: [
        {
          name: 'trichology:purify.science.items.0.name',
          code: 'purify 04.1',
          bestFor: 'trichology:purify.science.items.0.bestFor',
          benefit: 'trichology:purify.science.items.0.benefit',
          image: '/images/trichology/purify/oils/melaleuca.webp',
        },
        {
          name: 'trichology:purify.science.items.1.name',
          code: 'purify 04.2',
          bestFor: 'trichology:purify.science.items.1.bestFor',
          benefit: 'trichology:purify.science.items.1.benefit',
          image: '/images/trichology/purify/oils/lemongrass.webp',
        },
        {
          name: 'trichology:purify.science.items.2.name',
          code: 'purify 04.3',
          bestFor: 'trichology:purify.science.items.2.bestFor',
          benefit: 'trichology:purify.science.items.2.benefit',
          image: '/images/trichology/purify/oils/melissa.webp',
        },
      ],
    },

    brandStory: {
      eyebrow: 'trichology:purify.brandStory.eyebrow',
      title: 'trichology:purify.brandStory.title',
      body: 'trichology:purify.brandStory.body',
    },

    ritual: {
      eyebrow: 'trichology:purify.ritual.eyebrow',
      title: 'trichology:purify.ritual.title',
      outro: 'trichology:purify.ritual.outro',
      secondaryImage: '/images/trichology/purify/about-flatlay-v3.webp',
      steps: [
        {
          index: '01',
          title: 'trichology:purify.ritual.steps.0.title',
          desc: 'trichology:purify.ritual.steps.0.desc',
        },
        {
          index: '02',
          title: 'trichology:purify.ritual.steps.1.title',
          desc: 'trichology:purify.ritual.steps.1.desc',
        },
        {
          index: '03',
          title: 'trichology:purify.ritual.steps.2.title',
          desc: 'trichology:purify.ritual.steps.2.desc',
        },
        {
          index: '04',
          title: 'trichology:purify.ritual.steps.3.title',
          desc: 'trichology:purify.ritual.steps.3.desc',
        },
      ],
    },

    timeline: {
      eyebrow: 'trichology:purify.timeline.eyebrow',
      title: 'trichology:purify.timeline.title',
      note: 'trichology:purify.timeline.note',
      closer: 'trichology:purify.timeline.closer',
      items: [
        {
          label: 'trichology:purify.timeline.items.0.label',
          desc: 'trichology:purify.timeline.items.0.desc',
        },
        {
          label: 'trichology:purify.timeline.items.1.label',
          desc: 'trichology:purify.timeline.items.1.desc',
        },
        {
          label: 'trichology:purify.timeline.items.2.label',
          desc: 'trichology:purify.timeline.items.2.desc',
        },
        {
          label: 'trichology:purify.timeline.items.3.label',
          desc: 'trichology:purify.timeline.items.3.desc',
        },
      ],
    },

    pricing: {
      eyebrow: 'trichology:purify.pricing.eyebrow',
      title: 'trichology:purify.pricing.title',
      closer: {
        title: 'trichology:purify.pricing.closer.title',
        body: 'trichology:purify.pricing.closer.body',
      },
      tiers: [
        {
          id: 'diagnosis',
          label: 'trichology:purify.pricing.tiers.diagnosis.label',
          price: 'trichology:purify.pricing.tiers.diagnosis.price',
          includes: 'trichology:purify.pricing.tiers.diagnosis.includes',
          meta: 'trichology:purify.pricing.tiers.diagnosis.meta',
          tagline: 'trichology:purify.pricing.tiers.diagnosis.tagline',
        },
        {
          id: 'single',
          label: 'trichology:purify.pricing.tiers.single.label',
          price: 'trichology:purify.pricing.tiers.single.price',
          includes: 'trichology:purify.pricing.tiers.single.includes',
          meta: 'trichology:purify.pricing.tiers.single.meta',
          tagline: 'trichology:purify.pricing.tiers.single.tagline',
          badge: 'trichology:purify.pricing.tiers.single.badge',
        },
        {
          id: 'course',
          label: 'trichology:purify.pricing.tiers.course.label',
          price: 'trichology:purify.pricing.tiers.course.price',
          includes: 'trichology:purify.pricing.tiers.course.includes',
          meta: 'trichology:purify.pricing.tiers.course.meta',
          tagline: 'trichology:purify.pricing.tiers.course.tagline',
          badge: 'trichology:purify.pricing.tiers.course.badge',
          highlight: true,
        },
      ],
    },

    homeRoutine: {
      eyebrow: 'trichology:purify.homeRoutine.eyebrow',
      title: 'trichology:purify.homeRoutine.title',
      intro: 'trichology:purify.homeRoutine.intro',
      products: [
        {
          code: 'purify 02',
          name: 'trichology:purify.homeRoutine.products.0.name',
          size: 'trichology:purify.homeRoutine.products.0.size',
          role: 'trichology:purify.homeRoutine.products.0.role',
          shopPath: 'muran/purify-02',
        },
        {
          code: 'purify 03',
          name: 'trichology:purify.homeRoutine.products.1.name',
          size: 'trichology:purify.homeRoutine.products.1.size',
          role: 'trichology:purify.homeRoutine.products.1.role',
          shopPath: 'muran/purify-03',
        },
        {
          code: 'purify 05',
          name: 'trichology:purify.homeRoutine.products.2.name',
          size: 'trichology:purify.homeRoutine.products.2.size',
          role: 'trichology:purify.homeRoutine.products.2.role',
          shopPath: 'muran/purify-05',
        },
        {
          code: 'purify 07',
          name: 'trichology:purify.homeRoutine.products.3.name',
          size: 'trichology:purify.homeRoutine.products.3.size',
          role: 'trichology:purify.homeRoutine.products.3.role',
          shopPath: 'muran/purify-07',
        },
      ],
    },

    faq: {
      eyebrow: 'trichology:purify.faq.eyebrow',
      title: 'trichology:purify.faq.title',
      items: [
        {
          q: 'trichology:purify.faq.items.0.q',
          a: 'trichology:purify.faq.items.0.a',
        },
        {
          q: 'trichology:purify.faq.items.1.q',
          a: 'trichology:purify.faq.items.1.a',
        },
        {
          q: 'trichology:purify.faq.items.2.q',
          a: 'trichology:purify.faq.items.2.a',
        },
        {
          q: 'trichology:purify.faq.items.3.q',
          a: 'trichology:purify.faq.items.3.a',
        },
        {
          q: 'trichology:purify.faq.items.4.q',
          a: 'trichology:purify.faq.items.4.a',
        },
        {
          q: 'trichology:purify.faq.items.5.q',
          a: 'trichology:purify.faq.items.5.a',
        },
        {
          q: 'trichology:purify.faq.items.6.q',
          a: 'trichology:purify.faq.items.6.a',
        },
        {
          q: 'trichology:purify.faq.items.7.q',
          a: 'trichology:purify.faq.items.7.a',
        },
      ],
    },

    finalCta: {
      title: 'trichology:purify.finalCta.title',
      note: 'trichology:purify.finalCta.note',
      bookTreatment: 'trichology:purify.finalCta.bookTreatment',
      exploreOther: 'trichology:purify.finalCta.exploreOther',
    },
  },
  {
    slug: 'balancy',
    number: '03',
    name: 'Balancy',
    icon: Shield,
    image: '/images/trichology/balancy/about-flatlay.webp',
    aboutImageAspect: '922/766',
    hero: {
      eyebrow: 'trichology:balancy.hero.eyebrow',
      title: 'trichology:balancy.hero.title',
      subtitle: 'trichology:balancy.hero.subtitle',
      price: 'trichology:balancy.hero.price',
      duration: 'trichology:balancy.hero.duration',
    },
    about: {
      description: 'trichology:balancy.about.description',
      philosophyTitle: 'trichology:balancy.about.philosophyTitle',
      philosophy: 'trichology:balancy.about.philosophy',
    },
    benefits: [
      'trichology:balancy.benefits.0',
      'trichology:balancy.benefits.1',
      'trichology:balancy.benefits.2',
      'trichology:balancy.benefits.3',
      'trichology:balancy.benefits.4',
      'trichology:balancy.benefits.5',
      'trichology:balancy.benefits.6',
    ],
    process: [
      {
        title: 'trichology:balancy.process.0.title',
        desc: 'trichology:balancy.process.0.desc',
      },
      {
        title: 'trichology:balancy.process.1.title',
        desc: 'trichology:balancy.process.1.desc',
      },
      {
        title: 'trichology:balancy.process.2.title',
        desc: 'trichology:balancy.process.2.desc',
      },
      {
        title: 'trichology:balancy.process.3.title',
        desc: 'trichology:balancy.process.3.desc',
      },
    ],
    aftercare: 'trichology:balancy.aftercare',
    idealFor: 'trichology:balancy.idealFor',

    // ----- Upgrade fields -----
    heroPromise: 'trichology:balancy.heroPromise',
    heroSubline: 'trichology:balancy.heroSubline',
    heroImage: '/images/trichology/balancy/hero-muran.webp',
    heroImagePosition: 'left center',
    heroBadges: [
      {
        icon: 'microscope',
        title: 'trichology:balancy.heroBadges.0.title',
        body: 'trichology:balancy.heroBadges.0.body',
      },
      {
        icon: 'clock',
        title: 'trichology:balancy.heroBadges.1.title',
        body: 'trichology:balancy.heroBadges.1.body',
      },
      {
        icon: 'award',
        title: 'trichology:balancy.heroBadges.2.title',
        body: 'trichology:balancy.heroBadges.2.body',
      },
    ],

    symptoms: {
      title: 'trichology:balancy.symptoms.title',
      intro: 'trichology:balancy.symptoms.intro',
      items: [
        { text: 'trichology:balancy.symptoms.items.0.text' },
        { text: 'trichology:balancy.symptoms.items.1.text' },
        { text: 'trichology:balancy.symptoms.items.2.text' },
        { text: 'trichology:balancy.symptoms.items.3.text' },
        { text: 'trichology:balancy.symptoms.items.4.text' },
        { text: 'trichology:balancy.symptoms.items.5.text' },
        { text: 'trichology:balancy.symptoms.items.6.text' },
        { text: 'trichology:balancy.symptoms.items.7.text' },
      ],
    },

    science: {
      eyebrow: 'trichology:balancy.science.eyebrow',
      title: 'trichology:balancy.science.title',
      intro: 'trichology:balancy.science.intro',
      anchor: {
        name: 'trichology:balancy.science.anchor.name',
        benefit: 'trichology:balancy.science.anchor.benefit',
      },
      items: [
        {
          name: 'trichology:balancy.science.items.0.name',
          code: 'balancy 03.1',
          bestFor: 'trichology:balancy.science.items.0.bestFor',
          benefit: 'trichology:balancy.science.items.0.benefit',
          image: '/images/trichology/balancy/oils/cinnamon.webp',
        },
        {
          name: 'trichology:balancy.science.items.1.name',
          code: 'balancy 03.2',
          bestFor: 'trichology:balancy.science.items.1.bestFor',
          benefit: 'trichology:balancy.science.items.1.benefit',
          image: '/images/trichology/balancy/oils/citron.webp',
        },
        {
          name: 'trichology:balancy.science.items.2.name',
          code: 'balancy 03.3',
          bestFor: 'trichology:balancy.science.items.2.bestFor',
          benefit: 'trichology:balancy.science.items.2.benefit',
          image: '/images/trichology/balancy/oils/grapefruit.webp',
        },
      ],
    },

    brandStory: {
      eyebrow: 'trichology:balancy.brandStory.eyebrow',
      title: 'trichology:balancy.brandStory.title',
      body: 'trichology:balancy.brandStory.body',
    },

    ritual: {
      eyebrow: 'trichology:balancy.ritual.eyebrow',
      title: 'trichology:balancy.ritual.title',
      outro: 'trichology:balancy.ritual.outro',
      secondaryImage: '/images/trichology/balancy/about-flatlay.webp',
      steps: [
        {
          index: '01',
          title: 'trichology:balancy.ritual.steps.0.title',
          desc: 'trichology:balancy.ritual.steps.0.desc',
        },
        {
          index: '02',
          title: 'trichology:balancy.ritual.steps.1.title',
          desc: 'trichology:balancy.ritual.steps.1.desc',
        },
        {
          index: '03',
          title: 'trichology:balancy.ritual.steps.2.title',
          desc: 'trichology:balancy.ritual.steps.2.desc',
        },
        {
          index: '04',
          title: 'trichology:balancy.ritual.steps.3.title',
          desc: 'trichology:balancy.ritual.steps.3.desc',
        },
      ],
    },

    timeline: {
      eyebrow: 'trichology:balancy.timeline.eyebrow',
      title: 'trichology:balancy.timeline.title',
      note: 'trichology:balancy.timeline.note',
      closer: 'trichology:balancy.timeline.closer',
      items: [
        {
          label: 'trichology:balancy.timeline.items.0.label',
          desc: 'trichology:balancy.timeline.items.0.desc',
        },
        {
          label: 'trichology:balancy.timeline.items.1.label',
          desc: 'trichology:balancy.timeline.items.1.desc',
        },
        {
          label: 'trichology:balancy.timeline.items.2.label',
          desc: 'trichology:balancy.timeline.items.2.desc',
        },
        {
          label: 'trichology:balancy.timeline.items.3.label',
          desc: 'trichology:balancy.timeline.items.3.desc',
        },
      ],
    },

    pricing: {
      eyebrow: 'trichology:balancy.pricing.eyebrow',
      title: 'trichology:balancy.pricing.title',
      closer: {
        title: 'trichology:balancy.pricing.closer.title',
        body: 'trichology:balancy.pricing.closer.body',
      },
      tiers: [
        {
          id: 'diagnosis',
          label: 'trichology:balancy.pricing.tiers.diagnosis.label',
          price: 'trichology:balancy.pricing.tiers.diagnosis.price',
          includes: 'trichology:balancy.pricing.tiers.diagnosis.includes',
          meta: 'trichology:balancy.pricing.tiers.diagnosis.meta',
          tagline: 'trichology:balancy.pricing.tiers.diagnosis.tagline',
        },
        {
          id: 'single',
          label: 'trichology:balancy.pricing.tiers.single.label',
          price: 'trichology:balancy.pricing.tiers.single.price',
          includes: 'trichology:balancy.pricing.tiers.single.includes',
          meta: 'trichology:balancy.pricing.tiers.single.meta',
          tagline: 'trichology:balancy.pricing.tiers.single.tagline',
          badge: 'trichology:balancy.pricing.tiers.single.badge',
        },
        {
          id: 'course',
          label: 'trichology:balancy.pricing.tiers.course.label',
          price: 'trichology:balancy.pricing.tiers.course.price',
          includes: 'trichology:balancy.pricing.tiers.course.includes',
          meta: 'trichology:balancy.pricing.tiers.course.meta',
          tagline: 'trichology:balancy.pricing.tiers.course.tagline',
          badge: 'trichology:balancy.pricing.tiers.course.badge',
          highlight: true,
        },
      ],
    },

    homeRoutine: {
      eyebrow: 'trichology:balancy.homeRoutine.eyebrow',
      title: 'trichology:balancy.homeRoutine.title',
      intro: 'trichology:balancy.homeRoutine.intro',
      products: [
        {
          code: 'balancy 01',
          name: 'trichology:balancy.homeRoutine.products.0.name',
          size: 'trichology:balancy.homeRoutine.products.0.size',
          role: 'trichology:balancy.homeRoutine.products.0.role',
          shopPath: 'muran/balancy-01',
        },
        {
          code: 'balancy 02',
          name: 'trichology:balancy.homeRoutine.products.1.name',
          size: 'trichology:balancy.homeRoutine.products.1.size',
          role: 'trichology:balancy.homeRoutine.products.1.role',
          shopPath: 'muran/balancy-02',
        },
        {
          code: 'balancy 03',
          name: 'trichology:balancy.homeRoutine.products.2.name',
          size: 'trichology:balancy.homeRoutine.products.2.size',
          role: 'trichology:balancy.homeRoutine.products.2.role',
          shopPath: 'muran/balancy-03',
        },
        {
          code: 'balancy 04',
          name: 'trichology:balancy.homeRoutine.products.3.name',
          size: 'trichology:balancy.homeRoutine.products.3.size',
          role: 'trichology:balancy.homeRoutine.products.3.role',
          shopPath: 'muran/balancy-04',
        },
      ],
    },

    faq: {
      eyebrow: 'trichology:balancy.faq.eyebrow',
      title: 'trichology:balancy.faq.title',
      items: [
        {
          q: 'trichology:balancy.faq.items.0.q',
          a: 'trichology:balancy.faq.items.0.a',
        },
        {
          q: 'trichology:balancy.faq.items.1.q',
          a: 'trichology:balancy.faq.items.1.a',
        },
        {
          q: 'trichology:balancy.faq.items.2.q',
          a: 'trichology:balancy.faq.items.2.a',
        },
        {
          q: 'trichology:balancy.faq.items.3.q',
          a: 'trichology:balancy.faq.items.3.a',
        },
        {
          q: 'trichology:balancy.faq.items.4.q',
          a: 'trichology:balancy.faq.items.4.a',
        },
        {
          q: 'trichology:balancy.faq.items.5.q',
          a: 'trichology:balancy.faq.items.5.a',
        },
        {
          q: 'trichology:balancy.faq.items.6.q',
          a: 'trichology:balancy.faq.items.6.a',
        },
        {
          q: 'trichology:balancy.faq.items.7.q',
          a: 'trichology:balancy.faq.items.7.a',
        },
        {
          q: 'trichology:balancy.faq.items.8.q',
          a: 'trichology:balancy.faq.items.8.a',
        },
      ],
    },

    finalCta: {
      title: 'trichology:balancy.finalCta.title',
      note: 'trichology:balancy.finalCta.note',
      bookTreatment: 'trichology:balancy.finalCta.bookTreatment',
      exploreOther: 'trichology:balancy.finalCta.exploreOther',
    },
  },
  {
    slug: 'serenity',
    number: '04',
    name: 'Serenity',
    icon: Leaf,
    image: '/images/trichology/balancy/about-flatlay.webp',
    hero: {
      eyebrow: 'trichology:serenity.hero.eyebrow',
      title: 'trichology:serenity.hero.title',
      subtitle: 'trichology:serenity.hero.subtitle',
      price: 'trichology:serenity.hero.price',
      duration: 'trichology:serenity.hero.duration',
    },
    about: {
      description: 'trichology:serenity.about.description',
      philosophyTitle: 'trichology:serenity.about.philosophyTitle',
      philosophy: 'trichology:serenity.about.philosophy',
    },
    benefits: [
      'trichology:serenity.benefits.0',
      'trichology:serenity.benefits.1',
      'trichology:serenity.benefits.2',
      'trichology:serenity.benefits.3',
      'trichology:serenity.benefits.4',
      'trichology:serenity.benefits.5',
      'trichology:serenity.benefits.6',
      'trichology:serenity.benefits.7',
    ],
    process: [
      {
        title: 'trichology:serenity.process.0.title',
        desc: 'trichology:serenity.process.0.desc',
      },
      {
        title: 'trichology:serenity.process.1.title',
        desc: 'trichology:serenity.process.1.desc',
      },
      {
        title: 'trichology:serenity.process.2.title',
        desc: 'trichology:serenity.process.2.desc',
      },
      {
        title: 'trichology:serenity.process.3.title',
        desc: 'trichology:serenity.process.3.desc',
      },
    ],
    aftercare: 'trichology:serenity.aftercare',
    idealFor: 'trichology:serenity.idealFor',

    // ----- Upgrade fields -----
    heroPromise: 'trichology:serenity.heroPromise',
    heroSubline: 'trichology:serenity.heroSubline',
    heroImage: '/images/trichology/serenity/hero.webp',
    heroImagePosition: 'left 45%',
    heroBadges: [
      {
        icon: 'microscope',
        title: 'trichology:serenity.heroBadges.0.title',
        body: 'trichology:serenity.heroBadges.0.body',
      },
      {
        icon: 'clock',
        title: 'trichology:serenity.heroBadges.1.title',
        body: 'trichology:serenity.heroBadges.1.body',
      },
      {
        icon: 'award',
        title: 'trichology:serenity.heroBadges.2.title',
        body: 'trichology:serenity.heroBadges.2.body',
      },
    ],

    symptoms: {
      title: 'trichology:serenity.symptoms.title',
      intro: 'trichology:serenity.symptoms.intro',
      items: [
        { text: 'trichology:serenity.symptoms.items.0.text' },
        { text: 'trichology:serenity.symptoms.items.1.text' },
        { text: 'trichology:serenity.symptoms.items.2.text' },
        { text: 'trichology:serenity.symptoms.items.3.text' },
        { text: 'trichology:serenity.symptoms.items.4.text' },
        { text: 'trichology:serenity.symptoms.items.5.text' },
        { text: 'trichology:serenity.symptoms.items.6.text' },
        { text: 'trichology:serenity.symptoms.items.7.text' },
      ],
    },

    science: {
      eyebrow: 'trichology:serenity.science.eyebrow',
      title: 'trichology:serenity.science.title',
      intro: 'trichology:serenity.science.intro',
      anchor: {
        name: 'trichology:serenity.science.anchor.name',
        benefit: 'trichology:serenity.science.anchor.benefit',
      },
      items: [
        {
          name: 'trichology:serenity.science.items.0.name',
          code: 'serenity 02.1',
          bestFor: 'trichology:serenity.science.items.0.bestFor',
          benefit: 'trichology:serenity.science.items.0.benefit',
          image: '/images/trichology/serenity/oil-fennel.webp',
        },
        {
          name: 'trichology:serenity.science.items.1.name',
          code: 'serenity 02.2',
          bestFor: 'trichology:serenity.science.items.1.bestFor',
          benefit: 'trichology:serenity.science.items.1.benefit',
          image: '/images/trichology/serenity/oil-lavender.webp',
        },
        {
          name: 'trichology:serenity.science.items.2.name',
          code: 'serenity 02.3',
          bestFor: 'trichology:serenity.science.items.2.bestFor',
          benefit: 'trichology:serenity.science.items.2.benefit',
          image: '/images/trichology/serenity/oil-myrtle.webp',
        },
      ],
    },

    ritual: {
      eyebrow: 'trichology:serenity.ritual.eyebrow',
      title: 'trichology:serenity.ritual.title',
      outro: 'trichology:serenity.ritual.outro',
      secondaryImage: '/images/trichology/balancy/about-flatlay.webp',
      steps: [
        {
          index: '01',
          title: 'trichology:serenity.ritual.steps.0.title',
          desc: 'trichology:serenity.ritual.steps.0.desc',
        },
        {
          index: '02',
          title: 'trichology:serenity.ritual.steps.1.title',
          desc: 'trichology:serenity.ritual.steps.1.desc',
        },
        {
          index: '03',
          title: 'trichology:serenity.ritual.steps.2.title',
          desc: 'trichology:serenity.ritual.steps.2.desc',
        },
        {
          index: '04',
          title: 'trichology:serenity.ritual.steps.3.title',
          desc: 'trichology:serenity.ritual.steps.3.desc',
        },
      ],
    },

    timeline: {
      eyebrow: 'trichology:serenity.timeline.eyebrow',
      title: 'trichology:serenity.timeline.title',
      note: 'trichology:serenity.timeline.note',
      closer: 'trichology:serenity.timeline.closer',
      items: [
        {
          label: 'trichology:serenity.timeline.items.0.label',
          desc: 'trichology:serenity.timeline.items.0.desc',
        },
        {
          label: 'trichology:serenity.timeline.items.1.label',
          desc: 'trichology:serenity.timeline.items.1.desc',
        },
        {
          label: 'trichology:serenity.timeline.items.2.label',
          desc: 'trichology:serenity.timeline.items.2.desc',
        },
        {
          label: 'trichology:serenity.timeline.items.3.label',
          desc: 'trichology:serenity.timeline.items.3.desc',
        },
      ],
    },

    pricing: {
      eyebrow: 'trichology:serenity.pricing.eyebrow',
      title: 'trichology:serenity.pricing.title',
      closer: {
        title: 'trichology:serenity.pricing.closer.title',
        body: 'trichology:serenity.pricing.closer.body',
      },
      tiers: [
        {
          id: 'diagnosis',
          label: 'trichology:serenity.pricing.tiers.diagnosis.label',
          price: 'trichology:serenity.pricing.tiers.diagnosis.price',
          includes: 'trichology:serenity.pricing.tiers.diagnosis.includes',
          meta: 'trichology:serenity.pricing.tiers.diagnosis.meta',
          tagline: 'trichology:serenity.pricing.tiers.diagnosis.tagline',
        },
        {
          id: 'single',
          label: 'trichology:serenity.pricing.tiers.single.label',
          price: 'trichology:serenity.pricing.tiers.single.price',
          includes: 'trichology:serenity.pricing.tiers.single.includes',
          meta: 'trichology:serenity.pricing.tiers.single.meta',
          tagline: 'trichology:serenity.pricing.tiers.single.tagline',
          badge: 'trichology:serenity.pricing.tiers.single.badge',
        },
        {
          id: 'course',
          label: 'trichology:serenity.pricing.tiers.course.label',
          price: 'trichology:serenity.pricing.tiers.course.price',
          includes: 'trichology:serenity.pricing.tiers.course.includes',
          meta: 'trichology:serenity.pricing.tiers.course.meta',
          tagline: 'trichology:serenity.pricing.tiers.course.tagline',
          badge: 'trichology:serenity.pricing.tiers.course.badge',
          highlight: true,
        },
      ],
    },

    homeRoutine: {
      eyebrow: 'trichology:serenity.homeRoutine.eyebrow',
      title: 'trichology:serenity.homeRoutine.title',
      intro: 'trichology:serenity.homeRoutine.intro',
      products: [
        {
          code: 'serenity 01',
          name: 'trichology:serenity.homeRoutine.products.0.name',
          size: 'trichology:serenity.homeRoutine.products.0.size',
          role: 'trichology:serenity.homeRoutine.products.0.role',
          shopPath: 'muran/serenity-01',
        },
        {
          code: 'serenity 02',
          name: 'trichology:serenity.homeRoutine.products.1.name',
          size: 'trichology:serenity.homeRoutine.products.1.size',
          role: 'trichology:serenity.homeRoutine.products.1.role',
          shopPath: 'muran/serenity-02',
        },
        {
          code: 'serenity 03',
          name: 'trichology:serenity.homeRoutine.products.2.name',
          size: 'trichology:serenity.homeRoutine.products.2.size',
          role: 'trichology:serenity.homeRoutine.products.2.role',
          shopPath: 'muran/serenity-03',
        },
        {
          code: 'serenity 05',
          name: 'trichology:serenity.homeRoutine.products.3.name',
          size: 'trichology:serenity.homeRoutine.products.3.size',
          role: 'trichology:serenity.homeRoutine.products.3.role',
          shopPath: 'muran/serenity-05',
        },
      ],
    },

    faq: {
      eyebrow: 'trichology:serenity.faq.eyebrow',
      title: 'trichology:serenity.faq.title',
      items: [
        {
          q: 'trichology:serenity.faq.items.0.q',
          a: 'trichology:serenity.faq.items.0.a',
        },
        {
          q: 'trichology:serenity.faq.items.1.q',
          a: 'trichology:serenity.faq.items.1.a',
        },
        {
          q: 'trichology:serenity.faq.items.2.q',
          a: 'trichology:serenity.faq.items.2.a',
        },
        {
          q: 'trichology:serenity.faq.items.3.q',
          a: 'trichology:serenity.faq.items.3.a',
        },
        {
          q: 'trichology:serenity.faq.items.4.q',
          a: 'trichology:serenity.faq.items.4.a',
        },
        {
          q: 'trichology:serenity.faq.items.5.q',
          a: 'trichology:serenity.faq.items.5.a',
        },
        {
          q: 'trichology:serenity.faq.items.6.q',
          a: 'trichology:serenity.faq.items.6.a',
        },
        {
          q: 'trichology:serenity.faq.items.7.q',
          a: 'trichology:serenity.faq.items.7.a',
        },
        {
          q: 'trichology:serenity.faq.items.8.q',
          a: 'trichology:serenity.faq.items.8.a',
        },
        {
          q: 'trichology:serenity.faq.items.9.q',
          a: 'trichology:serenity.faq.items.9.a',
        },
      ],
    },

    aboutImageAspect: '922/766',

    finalCta: {
      title: 'trichology:serenity.finalCta.title',
      note: 'trichology:serenity.finalCta.note',
      bookTreatment: 'trichology:serenity.finalCta.bookTreatment',
      exploreOther: 'trichology:serenity.finalCta.exploreOther',
    },
  },
  {
    slug: 'wellness-ritual',
    number: '07',
    name: 'Wellness Ritual',
    icon: Star,
    image: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=1920&h=1080&fit=crop',
    hero: {
      eyebrow: 'trichology:wellness-ritual.hero.eyebrow',
      title: 'trichology:wellness-ritual.hero.title',
      subtitle: 'trichology:wellness-ritual.hero.subtitle',
      price: 'trichology:wellness-ritual.hero.price',
      duration: 'trichology:wellness-ritual.hero.duration',
    },
    about: {
      description: 'trichology:wellness-ritual.about.description',
      philosophyTitle: 'trichology:wellness-ritual.about.philosophyTitle',
      philosophy: 'trichology:wellness-ritual.about.philosophy',
    },
    benefits: [
      'trichology:wellness-ritual.benefits.0',
      'trichology:wellness-ritual.benefits.1',
      'trichology:wellness-ritual.benefits.2',
      'trichology:wellness-ritual.benefits.3',
    ],
    process: [
      {
        title: 'trichology:wellness-ritual.process.0.title',
        desc: 'trichology:wellness-ritual.process.0.desc',
      },
      {
        title: 'trichology:wellness-ritual.process.1.title',
        desc: 'trichology:wellness-ritual.process.1.desc',
      },
      {
        title: 'trichology:wellness-ritual.process.2.title',
        desc: 'trichology:wellness-ritual.process.2.desc',
      },
      {
        title: 'trichology:wellness-ritual.process.3.title',
        desc: 'trichology:wellness-ritual.process.3.desc',
      },
    ],
    aftercare: 'trichology:wellness-ritual.aftercare',
    idealFor: 'trichology:wellness-ritual.idealFor',
  },
]

export function getTrichologyBySlug(slug: string): TrichologyDetail | undefined {
  return trichologyDetails.find((t) => t.slug === slug)
}
